import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, ChevronLeft, FlaskConical, Plus, Trash2, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DangerDialog } from '@/components/DangerDialog'
import {
  deleteExperiment,
  fetchExperiments,
  previewExperiment,
  saveExperiment,
  type Experiment,
  type ExperimentVariant,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'
import { cn } from '@/lib/cn'

type Draft = { name: string; enabled: boolean; variants: ExperimentVariant[]; isNew: boolean }

const NEW_DRAFT: Draft = {
  name: '',
  enabled: true,
  variants: [{ key: 'control', weight: 1 }, { key: 'treatment', weight: 1 }],
  isNew: true,
}

/**
 * A14.09 + A14.10 — A/B tajribalar. Har tajriba nomi + variantlar (og'irlik) bilan.
 * Server (`GET /settings/features`) har foydalanuvchini og'irlik bo'yicha BARQAROR
 * variantga tayinlaydi (`experiments` xaritasi). «Taqsimot» = haqiqiy tayinlash-
 * funksiyasи orqali hisoblanган split (natija-metrika EMAS — u telemetriya kutadi).
 * Yozuv `POST /admin/settings/experiments` (audited). AUDIT_VIEW-gated.
 */
export function Experiments() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [items, setItems] = useState<Experiment[] | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [dist, setDist] = useState<Record<string, number> | null>(null)
  const [distSample, setDistSample] = useState(0)
  const [busy, setBusy] = useState(false)
  const [delTarget, setDelTarget] = useState<string | null>(null)

  const load = useCallback(() => {
    fetchExperiments()
      .then(setItems)
      .catch(() => setItems([]))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const edit = (e: Experiment) => {
    setDraft({ name: e.name, enabled: e.enabled, variants: e.variants.map((v) => ({ ...v })), isNew: false })
    setDist(null)
    previewExperiment(e.name)
      .then((p) => {
        setDist(p.distribution)
        setDistSample(p.sample)
      })
      .catch(() => setDist(null))
  }

  const save = () => {
    if (!draft || busy) return
    const variants = draft.variants.map((v) => ({ key: v.key.trim(), weight: Number(v.weight) || 0 })).filter((v) => v.key)
    if (!/^[a-z0-9_]{1,64}$/.test(draft.name.trim().toLowerCase())) {
      toast(t.exp.nameInvalid, { tone: 'error' })
      return
    }
    if (variants.length < 2) {
      toast(t.exp.needTwo, { tone: 'error' })
      return
    }
    setBusy(true)
    saveExperiment({ name: draft.name.trim().toLowerCase(), enabled: draft.enabled, variants })
      .then(() => {
        toast(t.exp.saved, { tone: 'success' })
        load()
        edit({ name: draft.name.trim().toLowerCase(), enabled: draft.enabled, variants })
      })
      .catch((e: unknown) => toast(t.exp.saveFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) }))
      .finally(() => setBusy(false))
  }

  const doDelete = async () => {
    if (!delTarget) return
    try {
      await deleteExperiment(delTarget)
      toast(t.exp.deleted, { tone: 'success' })
      if (draft?.name === delTarget) setDraft(null)
      load()
    } catch (e) {
      toast(t.exp.saveFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      throw e
    } finally {
      setDelTarget(null)
    }
  }

  const totalWeight = draft ? draft.variants.reduce((s, v) => s + (Number(v.weight) > 0 ? Number(v.weight) : 0), 0) : 0

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={t.exp.title}
        screenId="A14.09"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setDraft({ ...NEW_DRAFT, variants: NEW_DRAFT.variants.map((v) => ({ ...v })) })
                setDist(null)
              }}
              className="inline-flex items-center gap-1 rounded-md bg-accent px-2.5 py-1 text-sm font-medium text-white hover:bg-accent-hover"
            >
              <Plus size={14} />
              {t.exp.create}
            </button>
            <Link
              to="/flags"
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <ChevronLeft size={13} />
              {t.flags.title}
            </Link>
          </div>
        }
      />
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-auto p-3 lg:grid-cols-2">
        {/* List */}
        <div className="space-y-2">
          {items === null ? (
            <div className="p-6 text-center text-sm text-tertiary">…</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-tertiary">
              <FlaskConical size={24} className="mx-auto mb-2 opacity-60" />
              {t.exp.empty}
            </div>
          ) : (
            items.map((e) => (
              <button
                key={e.name}
                onClick={() => edit(e)}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left',
                  draft?.name === e.name && !draft.isNew ? 'border-accent bg-surface-2' : 'border-border-default bg-surface-1 hover:bg-surface-2',
                )}
              >
                <div className="min-w-0">
                  <div className="truncate font-mono text-sm text-primary">{e.name}</div>
                  <div className="mt-0.5 text-xs text-tertiary">{e.variants.map((v) => `${v.key}:${v.weight}`).join(' · ')}</div>
                </div>
                <span className={cn('ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs', e.enabled ? 'bg-accent-subtle text-accent' : 'bg-surface-3 text-tertiary')}>
                  {e.enabled ? t.exp.on : t.exp.off}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Editor */}
        {draft && (
          <div className="space-y-4 rounded-lg border border-border-default bg-surface-1 p-4">
            <div>
              <label className="mb-1 block text-xs text-tertiary">{t.exp.name}</label>
              <input
                value={draft.name}
                disabled={!draft.isNew}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="checkout_button_color"
                className="w-full rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 font-mono text-sm text-primary outline-none disabled:opacity-70"
              />
              {draft.isNew && <p className="mt-1 text-xs text-tertiary">{t.exp.nameHint}</p>}
            </div>

            <label className="flex items-center gap-2 text-sm text-secondary">
              <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
              {t.exp.enabled}
            </label>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs text-tertiary">{t.exp.variants}</label>
                <button
                  onClick={() => setDraft({ ...draft, variants: [...draft.variants, { key: '', weight: 1 }] })}
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                >
                  <Plus size={12} />
                  {t.exp.addVariant}
                </button>
              </div>
              <div className="space-y-1.5">
                {draft.variants.map((v, i) => {
                  const pct = totalWeight > 0 && Number(v.weight) > 0 ? Math.round((Number(v.weight) / totalWeight) * 100) : 0
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        value={v.key}
                        onChange={(e) => {
                          const nv = [...draft.variants]
                          nv[i] = { key: e.target.value, weight: nv[i]?.weight ?? 0 }
                          setDraft({ ...draft, variants: nv })
                        }}
                        placeholder="variant"
                        className="flex-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 font-mono text-sm text-primary outline-none"
                      />
                      <input
                        type="number"
                        min={0}
                        value={v.weight}
                        onChange={(e) => {
                          const nv = [...draft.variants]
                          nv[i] = { key: nv[i]?.key ?? '', weight: Number(e.target.value) }
                          setDraft({ ...draft, variants: nv })
                        }}
                        className="w-20 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-primary outline-none"
                      />
                      <span className="w-10 text-right text-xs tabular-nums text-tertiary">{pct}%</span>
                      <button
                        onClick={() => setDraft({ ...draft, variants: draft.variants.filter((_, j) => j !== i) })}
                        className="text-tertiary hover:text-danger"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="flex items-center gap-2 border-t border-border-subtle pt-3">
              <button onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
                {t.common.save}
              </button>
              {!draft.isNew && (
                <button onClick={() => setDelTarget(draft.name)} className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-danger hover:bg-surface-3">
                  <Trash2 size={14} />
                  {t.common.delete}
                </button>
              )}
            </div>

            {/* Assignment distribution (A14.10) */}
            {dist && (
              <div className="border-t border-border-subtle pt-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs text-tertiary">
                  <BarChart3 size={13} />
                  {t.exp.distribution(distSample)}
                </div>
                <div className="space-y-1.5">
                  {Object.entries(dist).map(([key, count]) => {
                    const pct = distSample > 0 ? (count / distSample) * 100 : 0
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className="w-24 shrink-0 truncate font-mono text-xs text-secondary" title={key}>{key}</span>
                        <div className="h-3 flex-1 overflow-hidden rounded bg-surface-3">
                          <div className="h-full rounded bg-accent" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-12 text-right text-xs tabular-nums text-tertiary">{pct.toFixed(1)}%</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <DangerDialog
        open={delTarget !== null}
        onClose={() => setDelTarget(null)}
        screenId="A14.09"
        level={2}
        title={t.exp.deleteTitle}
        description={delTarget ? t.exp.deleteDesc(delTarget) : ''}
        confirmLabel={t.common.delete}
        onConfirm={doDelete}
      />
    </>
  )
}
